import { useState } from 'react';

const officialActions = [
  {
    title: 'Register to Vote',
    text: 'Use Vote.gov to choose your state and reach the official registration instructions for where you live.',
    href: 'https://vote.gov/register',
  },
  {
    title: 'Verify Your Registration',
    text: 'Check your registration status before deadlines, address changes, or election-day surprises.',
    href: 'https://www.nass.org/can-I-vote/voter-registration-status',
  },
  {
    title: 'Find Your Polling Place',
    text: 'Look up where to vote and confirm local rules through official election resources.',
    href: 'https://www.nass.org/can-I-vote/find-your-polling-place',
  },
  {
    title: 'Request Absentee or Mail Voting Info',
    text: 'Review official absentee and mail ballot rules for your state before deadlines pass.',
    href: 'https://www.nass.org/can-I-vote/absentee-early-voting',
  },
];

const stateOptions = [
  ['alabama', 'Alabama'],
  ['alaska', 'Alaska'],
  ['arizona', 'Arizona'],
  ['arkansas', 'Arkansas'],
  ['california', 'California'],
  ['colorado', 'Colorado'],
  ['connecticut', 'Connecticut'],
  ['delaware', 'Delaware'],
  ['district-of-columbia', 'District of Columbia'],
  ['florida', 'Florida'],
  ['georgia', 'Georgia'],
  ['hawaii', 'Hawaii'],
  ['idaho', 'Idaho'],
  ['illinois', 'Illinois'],
  ['indiana', 'Indiana'],
  ['iowa', 'Iowa'],
  ['kansas', 'Kansas'],
  ['kentucky', 'Kentucky'],
  ['louisiana', 'Louisiana'],
  ['maine', 'Maine'],
  ['maryland', 'Maryland'],
  ['massachusetts', 'Massachusetts'],
  ['michigan', 'Michigan'],
  ['minnesota', 'Minnesota'],
  ['mississippi', 'Mississippi'],
  ['missouri', 'Missouri'],
  ['montana', 'Montana'],
  ['nebraska', 'Nebraska'],
  ['nevada', 'Nevada'],
  ['new-hampshire', 'New Hampshire'],
  ['new-jersey', 'New Jersey'],
  ['new-mexico', 'New Mexico'],
  ['new-york', 'New York'],
  ['north-carolina', 'North Carolina'],
  ['north-dakota', 'North Dakota'],
  ['ohio', 'Ohio'],
  ['oklahoma', 'Oklahoma'],
  ['oregon', 'Oregon'],
  ['pennsylvania', 'Pennsylvania'],
  ['rhode-island', 'Rhode Island'],
  ['south-carolina', 'South Carolina'],
  ['south-dakota', 'South Dakota'],
  ['tennessee', 'Tennessee'],
  ['texas', 'Texas'],
  ['utah', 'Utah'],
  ['vermont', 'Vermont'],
  ['virginia', 'Virginia'],
  ['washington', 'Washington'],
  ['west-virginia', 'West Virginia'],
  ['wisconsin', 'Wisconsin'],
  ['wyoming', 'Wyoming'],
] as const;

const stateRegistrationPortals = [
  { name: 'Alabama', url: 'https://www.sos.alabama.gov/alabama-votes/voter/register-to-vote', type: 'online' },
  { name: 'Alaska', url: 'https://voterregistration.alaska.gov/', type: 'online' },
  { name: 'Arizona', url: 'https://servicearizona.com/VoterRegistration/selectLanguage', type: 'online' },
  { name: 'Arkansas', url: 'https://www.sos.arkansas.gov/elections/voter-information/', type: 'paper' },
  { name: 'California', url: 'https://registertovote.ca.gov/', type: 'online' },
  { name: 'Colorado', url: 'https://www.sos.state.co.us/voter/pages/pub/olvr/findVoterReg.xhtml', type: 'online' },
  { name: 'Connecticut', url: 'https://voterregistration.ct.gov/OLVR/', type: 'online' },
  { name: 'Delaware', url: 'https://ivote.de.gov/', type: 'online' },
  { name: 'Florida', url: 'https://registertovoteflorida.gov/', type: 'online' },
  { name: 'Georgia', url: 'https://registertovote.sos.ga.gov/', type: 'online' },
  { name: 'Hawaii', url: 'https://olvr.hawaii.gov/', type: 'online' },
  { name: 'Idaho', url: 'https://apps.sos.idaho.gov/vote/olvr/', type: 'online' },
  { name: 'Illinois', url: 'https://ova.elections.il.gov/', type: 'online' },
  { name: 'Indiana', url: 'https://indianavoters.in.gov/', type: 'online' },
  { name: 'Iowa', url: 'https://mymvd.iowadot.gov/VoterRegistration', type: 'online' },
  { name: 'Kansas', url: 'https://www.kdor.ks.gov/Apps/VoterReg/', type: 'online' },
  { name: 'Kentucky', url: 'https://vrsws.sos.ky.gov/ovrweb/', type: 'online' },
  { name: 'Louisiana', url: 'https://voterportal.sos.la.gov/', type: 'online' },
  { name: 'Maine', url: 'https://www.maine.gov/sos/cec/elec/voter-info/voterguide.html', type: 'online' },
  { name: 'Maryland', url: 'https://voterservices.elections.maryland.gov/OnlineVoterRegistration/InstructionsStep1', type: 'online' },
  { name: 'Massachusetts', url: 'https://www.sec.state.ma.us/ovr/', type: 'online' },
  { name: 'Michigan', url: 'https://mvic.sos.state.mi.us/', type: 'online' },
  { name: 'Minnesota', url: 'https://mnvotes.sos.state.mn.us/VoterRegistration/VoterRegistrationMain.aspx', type: 'online' },
  { name: 'Mississippi', url: 'https://www.sos.ms.gov/elections-voting/voter-registration-information', type: 'paper' },
  { name: 'Missouri', url: 'https://www.sos.mo.gov/elections/goVoteMissouri/register', type: 'online' },
  { name: 'Montana', url: 'https://sosmt.gov/elections/vote/', type: 'paper' },
  { name: 'Nebraska', url: 'https://www.nebraska.gov/apps-sos-voter-registration/', type: 'online' },
  { name: 'Nevada', url: 'https://www.nvsos.gov/sos/elections/voters/registering-to-vote', type: 'online' },
  { name: 'New Hampshire', url: 'https://www.sos.nh.gov/elections/voters/register-vote', type: 'inperson' },
  { name: 'New Jersey', url: 'https://voter.svrs.nj.gov/register', type: 'online' },
  { name: 'New Mexico', url: 'https://portal.sos.state.nm.us/OVR/WebPages/InstructionsStep1.aspx', type: 'online' },
  { name: 'New York', url: 'https://voterreg.dmv.ny.gov/MotorVoter/', type: 'online' },
  { name: 'North Carolina', url: 'https://www.ncdot.gov/dmv/offices-services/online/Pages/voter-registration-application.aspx', type: 'online' },
  { name: 'North Dakota', url: 'https://vip.sos.nd.gov/', type: 'no-registration', note: 'Registration not required to vote' },
  { name: 'Ohio', url: 'https://olvr.ohiosos.gov/', type: 'online' },
  { name: 'Oklahoma', url: 'https://okvoterportal.okelections.us/', type: 'online' },
  { name: 'Oregon', url: 'https://secure.sos.state.or.us/orestar/vr/register.do', type: 'online' },
  { name: 'Pennsylvania', url: 'https://www.pavoterservices.pa.gov/pages/VoterRegistrationApplication.aspx', type: 'online' },
  { name: 'Rhode Island', url: 'https://vote.sos.ri.gov/', type: 'online' },
  { name: 'South Carolina', url: 'https://info.scvotes.sc.gov/eng/ovr/start.aspx', type: 'online' },
  { name: 'South Dakota', url: 'https://sdsos.gov/elections-voting/voting/voter-registration.aspx', type: 'paper' },
  { name: 'Tennessee', url: 'https://ovr.govote.tn.gov/', type: 'online' },
  { name: 'Texas', url: 'https://www.votetexas.gov/register-to-vote/', type: 'print-mail', note: 'Requires printing/mailing application' },
  { name: 'Utah', url: 'https://secure.utah.gov/voterreg/index.html', type: 'online' },
  { name: 'Vermont', url: 'https://olvr.vermont.gov/', type: 'online' },
  { name: 'Virginia', url: 'https://www.elections.virginia.gov/citizen-portal/', type: 'online' },
  { name: 'Washington', url: 'https://voter.votewa.gov/', type: 'online' },
  { name: 'West Virginia', url: 'https://ovr.sos.wv.gov/Register', type: 'online' },
  { name: 'Wisconsin', url: 'https://myvote.wi.gov/en-us/RegisterToVote', type: 'online' },
  { name: 'Wyoming', url: 'https://sos.wyo.gov/elections/state/registeringvote.aspx', type: 'paper' },
];

const planSteps = [
  'Confirm your registration status.',
  'Check registration, absentee, and early voting deadlines.',
  'Choose whether you will vote early, by mail, or on election day.',
  'Save your polling place, hours, ID rules, and transportation plan.',
  'Ask two friends or family members if they have checked their registration.',
];

export function VotePage() {
  const [selectedState, setSelectedState] = useState('');
  const selectedStateUrl = selectedState ? `https://vote.gov/register/${selectedState}` : '';

  return (
    <div className="page vote-page">
      <header className="page-header vote-hero">
        <p className="page-eyebrow">Voting Resources</p>
        <h1>Make sure your vote is counted.</h1>
        <p>
          Use official voter tools to register, verify your registration, find early voting
          options, and make a plan before deadlines arrive.
        </p>
      </header>

      <section className="state-lookup-panel">
        <div>
          <p className="section-eyebrow">Find your state</p>
          <h2>Go straight to official state voting instructions.</h2>
          <p>
            Choose your state to open its official Vote.gov registration page, which connects
            you to state election tools and deadlines.
          </p>
        </div>
        <div className="state-lookup-controls">
          <label htmlFor="state-select">Select your state</label>
          <select
            id="state-select"
            value={selectedState}
            onChange={(event) => setSelectedState(event.target.value)}
          >
            <option value="">Choose a state</option>
            {stateOptions.map(([slug, label]) => (
              <option key={slug} value={slug}>
                {label}
              </option>
            ))}
          </select>
          <a
            className={`btn btn-primary ${selectedState ? '' : 'btn-disabled'}`}
            href={selectedStateUrl || undefined}
            target="_blank"
            rel="noreferrer"
            aria-disabled={!selectedState}
          >
            Open State Resource
          </a>
        </div>
      </section>

      <section className="official-resource-grid" aria-label="Official voter resources">
        {officialActions.map((action) => (
          <a
            key={action.title}
            href={action.href}
            className="official-resource-card"
            target="_blank"
            rel="noreferrer"
          >
            <span>{action.title}</span>
            <p>{action.text}</p>
            <strong>Open official resource →</strong>
          </a>
        ))}
      </section>

      <section id="early-voting" className="vote-info-panel">
        <p className="section-eyebrow">Early and absentee voting</p>
        <h2>Do not wait until election day if you have another option.</h2>
        <p>
          Rules vary by state. Check early voting dates, mail ballot deadlines, and voter ID
          requirements through official election resources before making your plan.
        </p>
      </section>

      <section className="state-portals-section">
        <div className="section-header">
          <p className="section-eyebrow">Official voter registration portals by state</p>
          <h2>Direct links to each state&apos;s official registration system</h2>
          <p>
            If you prefer to go directly to a specific state&apos;s portal, here are the official
            links. Note: A few states (like North Dakota) do not require voter registration, while
            others require you to register by mail or in person.
          </p>
        </div>
        <div className="state-portals-table">
          {stateRegistrationPortals.map((state) => (
            <div key={state.name} className="state-portal-row">
              <div className="state-name">{state.name}</div>
              <div className="state-portal-link">
                <a
                  href={state.url}
                  target="_blank"
                  rel="noreferrer"
                  className={`portal-link ${state.type === 'no-registration' ? 'no-registration' : ''}`}
                >
                  {state.type === 'online' && 'Online Registration →'}
                  {state.type === 'paper' && 'Paper/In-Person Only'}
                  {state.type === 'print-mail' && 'Print & Mail Application'}
                  {state.type === 'inperson' && 'In-Person/Town Clerk'}
                  {state.type === 'no-registration' && 'Registration Not Required'}
                </a>
                {state.note && <span className="portal-note">{state.note}</span>}
              </div>
            </div>
          ))}
        </div>
        <div className="registration-tip">
          <strong>Tip:</strong> Keep in mind that voter registration deadlines, identification
          requirements, and mail-in voting rules change rapidly from state to state. Always
          double-check your local deadlines well ahead of an upcoming election!
        </div>
      </section>

      <section id="make-plan" className="vote-plan-panel">
        <div>
          <p className="section-eyebrow">Make a voting plan</p>
          <h2>Five steps before you vote</h2>
        </div>
        <ol>
          {planSteps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </section>
    </div>
  );
}
